import React, { useEffect, useId, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToHero } from "../lib/scroll";

const linkBase =
  "text-sm font-medium text-gold-100/95 hover:text-white transition-colors";
const activeLink =
  "text-white font-semibold underline underline-offset-4 decoration-gold-400/90";

const navLinkClass = ({ isActive }) =>
  `${linkBase} ${isActive ? activeLink : ""}`;

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuPanelId = useId();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  /** Already on home with no hash — NavLink won't navigate; scroll to hero manually */
  const handleHomeClick = (e) => {
    closeMenu();
    if (location.pathname === "/" && !location.hash) {
      e.preventDefault();
      scrollToHero();
    }
  };

  return (
    <header
      className="sticky top-0 z-50 w-full bg-burgundy-700 border-b border-burgundy-600 shadow-[0_4px_24px_-10px_rgba(0,0,0,0.35)] relative"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 min-h-[6rem] sm:min-h-[6.75rem] py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4">
        <Link
          to="/"
          onClick={handleHomeClick}
          className="flex items-center group shrink-0 min-w-0"
          data-testid="nav-home-link"
        >
          <img
            src="/bcsabha-logo.png"
            alt="Bombay Catholic Sabha"
            className="h-[96px] sm:h-[114px] md:h-[130px] lg:h-[148px] w-auto max-w-[min(620px,calc(100vw-5.75rem))] md:max-w-[min(620px,90vw)] object-contain object-left drop-shadow-md"
            width={620}
            height={148}
            decoding="async"
          />
        </Link>

        <nav
          className="hidden md:flex items-center gap-6 lg:gap-8 shrink-0"
          aria-label="Primary"
        >
          <NavLink
            to="/"
            end
            onClick={handleHomeClick}
            className={navLinkClass}
            data-testid="nav-link-home"
          >
            Home
          </NavLink>
          <NavLink
            to="/sponsors"
            className={navLinkClass}
            data-testid="nav-link-sponsors"
          >
            Sponsors
          </NavLink>
          <Link
            to={{ pathname: "/", hash: "about" }}
            className={linkBase}
            data-testid="nav-link-about"
          >
            About
          </Link>
          <Link
            to={{ pathname: "/", hash: "contact" }}
            className={linkBase}
            data-testid="nav-link-contact"
          >
            Contact
          </Link>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/sponsors"
            className="hidden md:inline-flex items-center rounded-lg bg-gold-500 hover:bg-gold-400 text-burgundy-900 text-sm font-semibold px-4 py-2 shadow-lg shadow-black/20 transition-colors"
            data-testid="nav-cta-sponsors"
          >
            Souvenir Directory
          </Link>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-lg border border-gold-400/45 bg-burgundy-800/70 p-2.5 text-gold-50 hover:bg-burgundy-900 hover:border-gold-400/60 shadow-md transition-colors"
            aria-expanded={menuOpen}
            aria-controls={menuPanelId}
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <nav
          id={menuPanelId}
          className="md:hidden absolute left-0 right-0 top-full z-[100] border-t border-burgundy-600 border-b border-burgundy-700 bg-burgundy-800 shadow-xl"
          aria-label="Mobile primary navigation"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
            <NavLink
              to="/"
              end
              onClick={handleHomeClick}
              className={({ isActive }) =>
                `${navLinkClass({ isActive })} py-3 px-3 rounded-lg hover:bg-burgundy-700/90`
              }
              data-testid="nav-mobile-link-home"
            >
              Home
            </NavLink>
            <NavLink
              to="/sponsors"
              onClick={closeMenu}
              className={({ isActive }) =>
                `${navLinkClass({ isActive })} py-3 px-3 rounded-lg hover:bg-burgundy-700/90`
              }
              data-testid="nav-mobile-link-sponsors"
            >
              Sponsors
            </NavLink>
            <Link
              to={{ pathname: "/", hash: "about" }}
              onClick={closeMenu}
              className={`${linkBase} py-3 px-3 rounded-lg hover:bg-burgundy-700/90`}
              data-testid="nav-mobile-link-about"
            >
              About
            </Link>
            <Link
              to={{ pathname: "/", hash: "contact" }}
              onClick={closeMenu}
              className={`${linkBase} py-3 px-3 rounded-lg hover:bg-burgundy-700/90`}
              data-testid="nav-mobile-link-contact"
            >
              Contact
            </Link>
            <Link
              to="/sponsors"
              onClick={closeMenu}
              className="mt-3 inline-flex items-center justify-center rounded-lg bg-gold-500 hover:bg-gold-400 text-burgundy-900 text-sm font-semibold px-4 py-3 shadow-lg shadow-black/25 transition-colors"
              data-testid="nav-mobile-cta-sponsors"
            >
              Souvenir Directory
            </Link>
          </div>
        </nav>
      ) : null}
    </header>
  );
}
